import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import type { ReactElement } from 'react'
import { Link, Navigate } from 'react-router'

import { parentReadDevicesOptions, parentReadFamilyOptions } from '@/client/@tanstack/react-query.gen'
import { DeviceOnlineIndicator } from '@/components/features/device-online-indicator'
import { Skeleton } from '@/components/ui/skeleton'
import { parentQueryDefaults } from '@/lib/parent-query'

export function DeviceList(): ReactElement {
  const family = useQuery({ ...parentReadFamilyOptions(), ...parentQueryDefaults })
  const devices = useQuery({ ...parentReadDevicesOptions(), ...parentQueryDefaults })
  const list = devices.data ?? []

  if (devices.isPending) {
    return (
      <div role="status" aria-label="Loading devices" className="space-y-2">
        <Skeleton className="h-9 w-48" />
        {Array.from({ length: 3 }, (_, row) => (
          <Skeleton key={row} className="h-14 w-full" />
        ))}
      </div>
    )
  }

  if (list.length === 1) {
    return <Navigate to={`/devices/${list[0].id}`} replace />
  }

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold tracking-tight">{family.data?.name ?? 'Monitor'}</h1>
      {devices.isError ? (
        <p role="alert" className="text-destructive text-sm">
          Devices could not be loaded.
        </p>
      ) : list.length === 0 ? (
        <p className="text-muted-foreground">No devices yet.</p>
      ) : (
        <ul className="divide-border divide-y rounded-lg border">
          {list.map((device) => (
            <li key={device.id}>
              <Link
                to={`/devices/${device.id}`}
                viewTransition
                className="hover:bg-accent flex items-center justify-between gap-3 p-4 active:opacity-70 [-webkit-tap-highlight-color:transparent]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <DeviceOnlineIndicator lastHeartbeatAt={device.last_heartbeat_at} showLabel={false} />
                  <span className="truncate font-medium">{device.name}</span>
                </span>
                <ChevronRight aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
